/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { LineChart } from 'echarts/charts';
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useNodeResourceUtilizationQuery } from '@/hooks/query/useNodeResourceUtilizationQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { formatTimestamp } from '@/pages/NodeDetails/Telemetry/formatHelpers';
import { Switch } from '@/primitives/Switch';

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const LAST_HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

function currentMinute(): number {
  return Math.floor(Date.now() / MINUTE_MS) * MINUTE_MS;
}

export type ResourceUtilizationChartProps = {
  nodeId: string;
};

type ChartRow = {
  bucket: string;
  cpuUserPct: number;
  memPct: number;
};

type TooltipParam = {
  axisValue?: string;
  name: string;
  seriesName: string;
  value: number;
  color: string;
};

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function buildOption(rows: ChartRow[]): echarts.EChartsCoreOption {
  const times = rows.map((r) => r.bucket);

  return {
    animation: false,
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      appendToBody: true,
      formatter: (params: TooltipParam[]) => {
        if (!params.length) return '';
        const header = formatTimestamp(params[0].axisValue ?? params[0].name);
        const lines = params.map((p) => {
          const dot = `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${escapeHtml(
            p.color,
          )};margin-right:5px"></span>`;
          return `${dot}${escapeHtml(p.seriesName)}: <b>${p.value.toFixed(2)}%</b>`;
        });
        return [header, ...lines].join('<br>');
      },
    },
    legend: { top: 0, right: 10, textStyle: { fontSize: 11 } },
    grid: { left: 50, right: 20, top: 30, bottom: 30 },
    xAxis: {
      type: 'category',
      data: times,
      axisLine: { lineStyle: { color: '#e5e5e5' } },
      axisTick: { show: false },
      axisLabel: {
        color: '#666',
        fontSize: 9,
        formatter: (v: string) => formatTimestamp(v),
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: '#666', fontSize: 9, formatter: (v: number) => `${v}%` },
      splitLine: { lineStyle: { color: '#f0f0f0' } },
    },
    // No absolute-bytes series: mimic-agent currently reports
    // system.memory.usage as a cumulative counter (not a gauge), so the value
    // grows without bound. Re-add an absolute-memory line once host RAM total
    // is exposed via system_profile or a corrected metric.
    series: [
      {
        name: 'User CPU %',
        type: 'line',
        data: rows.map((r) => r.cpuUserPct),
        smooth: true,
        showSymbol: false,
        itemStyle: { color: '#1890ff' },
      },
      {
        name: 'Memory %',
        type: 'line',
        data: rows.map((r) => r.memPct),
        smooth: true,
        showSymbol: false,
        itemStyle: { color: '#52c41a' },
      },
    ],
  };
}

export function ResourceUtilizationChart({ nodeId }: ResourceUtilizationChartProps) {
  const { t } = useMimicTranslation('node');
  // v1 shows the last hour. Anchor `until` to the most recent minute boundary
  // and advance once per minute so a dwelling user sees the right edge of the
  // chart keep up with the wall clock. Quantizing to the minute keeps the
  // React Query key stable between ticks.
  const [minuteAnchor, setMinuteAnchor] = useState(currentMinute);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => setMinuteAnchor(currentMinute()), MINUTE_MS);
    return () => clearInterval(id);
  }, [autoRefresh]);

  const { since, until } = useMemo(
    () => ({
      since: new Date(minuteAnchor - LAST_HOUR_MS).toISOString(),
      until: new Date(minuteAnchor).toISOString(),
    }),
    [minuteAnchor],
  );

  const query = useNodeResourceUtilizationQuery({
    nodeId,
    since,
    until,
    refetchIntervalMs: autoRefresh ? 60_000 : false,
  });

  const chartRef = useRef<HTMLDivElement | null>(null);
  const instanceRef = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current) return undefined;
    instanceRef.current = echarts.init(chartRef.current);
    const ro = new ResizeObserver(() => instanceRef.current?.resize());
    ro.observe(chartRef.current);
    return () => {
      ro.disconnect();
      instanceRef.current?.dispose();
      instanceRef.current = null;
    };
  }, []);

  const option = useMemo(() => (query.data ? buildOption(query.data.data) : null), [query.data]);

  useEffect(() => {
    if (!instanceRef.current || !option) return;
    instanceRef.current.setOption(option, true);
  }, [option]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: '#666' }}>{t('autoRefresh')}</span>
        <Switch
          checked={autoRefresh}
          onChange={(value) => setAutoRefresh(value)}
          dataTestId="resource-utilization-auto-refresh"
        />
      </div>
      <div
        data-testid="resource-utilization-chart"
        ref={chartRef}
        style={{ width: '100%', height: 240, opacity: query.isLoading ? 0.5 : 1 }}
      />
    </div>
  );
}
